import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { refreshPins } from "../utils/refresh_pins"

// "Add pin" modal on the Edit-pinned page. Two ways in: search the user's own
// contacts/tasks/messages (GET /pins/search) and click a result, or paste a
// website URL to pin a link. Both POST /pins with the same contract the inline
// pin_controller uses; on success we reload so the new card renders server-side.
export default class extends Controller {
    static values = { searchUrl: String, createUrl: String }
    static targets = ["modal", "input", "results", "url", "title", "favicon", "error", "grid", "folderTemplate", "folderName", "folderError"]

    // ?add=1 — the search omnibox's "Pin something" quick action deep-links
    // straight into this modal. The param is stripped so a reload or Back
    // doesn't reopen it.
    connect() {
        let add = null
        try { add = new URLSearchParams(window.location.search).get("add") } catch { return }
        if (!add) return

        try {
            const url = new URL(window.location.href)
            url.searchParams.delete("add")
            window.history.replaceState({}, "", url)
        } catch { /* ignore */ }

        requestAnimationFrame(() => this.open())
    }

    open(event) {
        event?.preventDefault()
        this.modalTarget.classList.remove("hidden")
        this._clearError()
        requestAnimationFrame(() => this.inputTarget?.focus())
        this._boundKeydown = this._boundKeydown || ((e) => { if (e.key === "Escape") this.close() })
        document.addEventListener("keydown", this._boundKeydown)
    }

    close(event) {
        event?.preventDefault()
        this.modalTarget.classList.add("hidden")
        if (this._boundKeydown) document.removeEventListener("keydown", this._boundKeydown)
    }

    backdropClose(event) {
        if (event.target === this.modalTarget) this.close()
    }

    // ---- Inline "new folder" creator card ----
    // Instead of a modal, we clone a card-shaped creator into the grid so a new
    // folder looks like the folder cards it joins. Enter or the ✓ button POSTs
    // the create endpoint; Escape or blurring an empty input removes the card.
    newFolder(event) {
        event?.preventDefault()
        const existing = this._creatorCard()
        if (existing) {
            existing.querySelector("[data-add-pin-target='folderName']")?.focus()
            return
        }
        const fragment = this.folderTemplateTarget.content.cloneNode(true)
        this.gridTarget.appendChild(fragment)
        const card = this._creatorCard()
        card?.scrollIntoView({ behavior: "smooth", block: "nearest" })
        requestAnimationFrame(() => this.folderNameTarget?.focus())
    }

    cancelFolder(event) {
        event?.preventDefault()
        this._creatorCard()?.remove()
    }

    // Blur that leaves the field empty tears the creator card down; a filled
    // field is left alone so clicking the ✓ button (which blurs first) can still
    // submit.
    folderBlur() {
        setTimeout(() => {
            const card = this._creatorCard()
            if (card && !this.folderNameTarget?.value.trim()) card.remove()
        }, 150)
    }

    _creatorCard() {
        return this.element.querySelector(".home-pin-folder--creator")
    }

    createFolder(event) {
        event?.preventDefault()
        const name = this.folderNameTarget.value.trim()
        if (!name) {
            this._showFolderError("Enter a folder name")
            return
        }
        this._clearFolderError()
        const trigger = event.currentTarget
        if (trigger?.disabled) return
        if (trigger) trigger.disabled = true
        fetch(this.createUrlValue, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ pinable_type: "Folder", metadata: { name } })
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not create folder")
                refreshPins(this.element)
            })
            .catch((err) => {
                this._showFolderError(err.message || "Could not create folder")
                if (trigger) trigger.disabled = false
            })
    }

    _showFolderError(message) {
        if (!this.hasFolderErrorTarget) return
        this.folderErrorTarget.textContent = message
        this.folderErrorTarget.classList.remove("hidden")
    }

    _clearFolderError() {
        if (!this.hasFolderErrorTarget) return
        this.folderErrorTarget.textContent = ""
        this.folderErrorTarget.classList.add("hidden")
    }

    search() {
        clearTimeout(this._debounce)
        const q = this.inputTarget.value.trim()
        if (q.length < 2) {
            this.resultsTarget.innerHTML = ""
            return
        }
        this._debounce = setTimeout(() => this._runSearch(q), 180)
    }

    _runSearch(q) {
        fetch(`${this.searchUrlValue}?q=${encodeURIComponent(q)}`, {
            headers: { Accept: "application/json" },
            credentials: "same-origin"
        })
            .then((r) => r.json())
            .then((data) => this._renderResults(data.groups || []))
            .catch(() => { this.resultsTarget.innerHTML = "" })
    }

    _renderResults(groups) {
        if (!groups.length) {
            this.resultsTarget.innerHTML = `<p class="pins-add-empty">No matches yet.</p>`
            return
        }
        this.resultsTarget.innerHTML = groups.map((group) => `
            <div class="pins-add-group">
                <p class="pins-add-group-label">${this._escape(group.label)}</p>
                ${group.items.map((item) => this._resultRow(item)).join("")}
            </div>
        `).join("")
    }

    _resultRow(item) {
        return `
            <button type="button" class="pins-add-result" data-action="add-pin#pinResult"
                data-pinable-type="${this._escape(item.pinable_type)}" data-pinable-id="${this._escape(item.pinable_id)}">
                <span class="pins-add-result-icon"><i class="bi ${this._escape(item.icon || "bi-pin")}"></i></span>
                <span class="pins-add-result-copy">
                    <span class="pins-add-result-title">${this._escape(item.title)}</span>
                    ${item.sublabel ? `<span class="pins-add-result-sub">${this._escape(item.sublabel)}</span>` : ""}
                </span>
            </button>`
    }

    pinResult(event) {
        const el = event.currentTarget
        this._pin({ pinable_type: el.dataset.pinableType, pinable_id: el.dataset.pinableId, metadata: {} }, el)
    }

    urlChanged() {
        // Prefill the title from the URL host once, leaving further edits alone.
        if (this.titleTarget.value.trim() || this._titleTouched) return
        try {
            let value = this.urlTarget.value.trim()
            if (!value) return
            if (!/^https?:\/\//i.test(value)) value = `https://${value}`
            this.titleTarget.value = new URL(value).host.replace(/^www\./, "")
        } catch { /* not a URL yet */ }
    }

    titleTouched() {
        this._titleTouched = true
    }

    pinLink(event) {
        event?.preventDefault()
        const url = this.urlTarget.value.trim()
        if (!url) {
            this._showError("Enter a website URL")
            return
        }
        // The icon is optional and the server falls back to the derived favicon
        // when it is blank or unusable, so it rides along as-is.
        const favicon = this.hasFaviconTarget ? this.faviconTarget.value.trim() : ""
        this._pin({
            pinable_type: "Link",
            pinable_id: url,
            metadata: { url, title: this.titleTarget.value.trim(), favicon }
        }, event.currentTarget)
    }

    _pin(body, trigger) {
        this._clearError()
        if (trigger) trigger.disabled = true
        fetch(this.createUrlValue, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify(body)
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not add pin")
                refreshPins(this.element)
            })
            .catch((err) => {
                this._showError(err.message || "Could not add pin")
                if (trigger) trigger.disabled = false
            })
    }

    _showError(message) {
        if (!this.hasErrorTarget) return
        this.errorTarget.textContent = message
        this.errorTarget.classList.remove("hidden")
    }

    _clearError() {
        if (!this.hasErrorTarget) return
        this.errorTarget.textContent = ""
        this.errorTarget.classList.add("hidden")
    }

    _escape(value) {
        const div = document.createElement("div")
        div.textContent = value == null ? "" : String(value)
        return div.innerHTML
    }
}
